import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import FullScreenActivityFrame from '../../../components/activity-frame/full-screen-activity-spinner';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { SiteCard } from '../../../components/site/SiteCard';
import { SiteDetails } from '../../../components/site/SiteDetails';
import TableTab from '../../../components/tabs/table-tab';
import { toast } from "react-toastify";
import { DEF_PG_SIZE_SUB } from '../../../constants/maximum-page-size';
import { useLocale } from '../../../contexts/localeContext';
import { TableContextProvider } from '../../../contexts/table/TableContextProvider';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { Button } from '../../../components/button/Button';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import SiteCodeColumns from '../../../models/ColumnDefinitions/SiteCodeColumns';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { ListData } from '../../../models/ListData';
import { ListQuery } from '../../../models/ListQuery';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import SiteCodeSearch from '../../../models/search/SiteCodeSearch';
import { DeleteEntityResult } from '../../../models/services/DeleteEntityResult';
import { Site as SiteModel } from '../../../models/Site';
import { TabSummary } from '../../../models/TabSummary';
import { deleteSiteCode, getSiteById, getSiteCodesBySiteId, getSiteNamesWithPagination, saveSiteCode, storeSite } from '../../../services/siteService';
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { setSiteAction, updateSitesAction } from '../../../store/site/actions';
import { IAppState } from '../../../store/types';
import { SiteCodeModal } from './SiteCodeModal';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

// --> variables to update
const allItemsPath = '/access-control/configuration/sites';
const itemPath = '/access-control/configuration/site/';
const initialItem: SiteModel = {
    id: 0,
    name: '',
    hostIP: '',
    wanIP: '',
    tagmask: 'FFFFFFFFFFFFF',
    tagmaskRemovezeros: false,
    timeZone: '',
    timeZoneId: 0,
    description: '',
    default: undefined,
    ossValidityDuration: 336, // default to 2 weeks
}
// <--

const Site: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.siteCodes), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<SiteModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const [showSiteCodeWizard, setShowSiteCodeWizard] = useState(false);
    const [siteCodeBusy, setSiteCodeBusy] = useState(false);
    const [currentSiteCode, setCurrentSiteCode] = useState<any>({})
    const dispatch = useDispatch();
    const siteId = useSelector((state: IAppState) => state.site.site.value)
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    // --> variables and methods to update

    const openSiteCodeModal = async (e) => {
        try {
            const siteCodes = await getNewSiteCodesBySiteId(itemId)
            const selectedSiteCode = siteCodes.rows.find(item => item.id === e) as any
            setCurrentSiteCode(selectedSiteCode)
            setShowSiteCodeWizard(true);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { setShowSiteCodeWizard(false) });
        }
    }

    const onSiteCodeSave = (storeState) => {
        setSiteCodeBusy(true);
        saveSiteCode(itemId, storeState.siteCode, currentSiteCode ? false : true, currentSiteCode ? currentSiteCode.id : null)
            .then(() => {
                toast.success(translate.byKey(TranslationKeys.siteCodeSavedSuccessfully));
                if (!currentSiteCode) {
                    const updatedTabs = updateTabBadgeTotal(1, tabs[1].badge + 1, initialTabs);
                    setTabs(updatedTabs);
                }
                dataRefresh((query) => getSiteCodesBySiteId(query, itemId));
                setShowSiteCodeWizard(false);
            })
            .catch((e) =>
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(e) })
            )
            .finally(() => {
                setSiteCodeBusy(false);
            });
    }

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteSiteCode(id)
                .then(deleteResult => {
                    resolve(deleteResult);
                    setTabs(updateTabBadgeTotal(1, tabs[1].badge - 1, tabs));
                    setBusy(false);
                    dataRefresh((query) => getSiteCodesBySiteId(query, itemId));
                },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    useEffect(() => {
        dataRefresh((query) => getSiteCodesBySiteId(query, itemId));
    }, [itemId])

    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    const getNewSiteCodesBySiteId = (listQuery) => {
        return getSiteCodesBySiteId(listQuery, itemId)
    }
    /**
     * Content and descriptors for each tab
     */
    const tabContentList: ContentWrapper[] = [{
        content: <SiteDetails />
    }, {
        content: <>
            <TableContextProvider
                dataMethod={(listQuery) => getSiteCodesBySiteId(listQuery, itemId)}
                tableDescriptor={SiteCodeColumns}
                searchDescriptor={SiteCodeSearch}
                useTable
                usePagination
                useFilter
                useSearch
                deleteMethod={onDelete}
                customDeleteKey={{ idKey: 'siteCode', nameKey: 'code' }}
                manualDataRefresh={manualDataRefresh}
                editMethod={openSiteCodeModal}
                useSort
                maxPageSize={DEF_PG_SIZE_SUB}
                onAddNew={() => setShowSiteCodeWizard(true)}>
                <TableTab
                    title={translate.byKey(TranslationKeys.assignedSiteCodes)}
                    selectRows={false}
                    cyName="site">
                    <Button
                        variant="submit"
                        shape="rounded"
                        id='assignSiteCodeBtnId'
                        className="py-1 px-5"
                        onClick={openSiteCodeModal}>
                        {translate.byKey(TranslationKeys.manage)}
                    </Button>
                </TableTab>
            </TableContextProvider>
            <SiteCodeModal onClose={() => setShowSiteCodeWizard(false)} isOpen={showSiteCodeWizard} onSave={onSiteCodeSave} busy={siteCodeBusy} currentSiteCode={currentSiteCode?.code ?? 0}></SiteCodeModal>

        </>
    },
    {
        content: <Description title={translate.byKey(TranslationKeys.notes)} />
    }];

    const topic: Topic = {
        accessor: 'siteName',
        title: translate.byKey(TranslationKeys.sites),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    }

    const reloadPage = async () => {
        try {
            if (itemId) {
                const site = await getSiteById(itemId);
                const siteCodes = await getSiteCodesBySiteId(undefined, itemId)
                const updatedTabs = updateTabBadgeTotal(1, siteCodes.rows.length, initialTabs);

                setTabs(updatedTabs);
                setSelectedItem({label: site.name, value: site.id.toString(), isDefault: site.isDefault})
                setCurrentItem(site);

                setBreadcrumb([
                    { value: '/access-control/configuration', displayName: translate.byKey(TranslationKeys.configuration), disableClick: true },
                    { value: allItemsPath, displayName: translate.byKey(TranslationKeys.sites) },
                    { value: itemPath + `${itemId}`, displayName: `${site?.name ?? ''}` }
                ]);

                return;
            }
            else {
                setCurrentItem(initialItem);
            }

            setTabs([initialTabs[0]]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => {history.push(allItemsPath)});
        }
    }

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    /**
     * Save details when the form is submitted
     * @param values
     */
    const saveSite = (values) => {
        const site: SiteModel = {
            ...currentItem,
            id: +itemId,
            name: values.name,
            hostIP: values.hostIP,
            wanIP: values.wanIP,
            tagmask: values.tagmask,
            tagmaskRemovezeros: values.tagmaskRemovezeros,
            timeZone: values.timeZone,
            timeZoneId: values.timeZoneId,
            description: values.description,
            ossId: values.ossId && values.ossId > 0 ? values.ossId : 0, // If ossId is undefined or null, default to 0
            ossValidityDuration: values.ossValidityDuration
        };
        const createNewSite = itemId ? +itemId < 0 : true;

        setBusy(true);
        storeSite(site, createNewSite).then(async (value: any) => {
            if (value.id === +siteId) {
                dispatch(setSiteAction({ label: site.name, value: site.id }));
            }
            dispatch(updateSitesAction(true));

            setCurrentItem(site);
            history.push(`${itemPath}${value.id}`);
            toast.success(translate.byKey(TranslationKeys.siteSaved));
            reloadPage();
        }).catch(failure => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
        }).finally(() => {
            setBusy(false);
        });
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const getTabContent = (index?: number) => {
        const selectedTabContent = tabContentList[index ?? 0];
        return selectedTabContent.content;
    };

    return <>
        {busy ? <FullScreenActivityFrame message={translate.byKey(TranslationKeys.saveInProgress)} /> : null}
        <TopicContextProvider dataMethod={getSiteNamesWithPagination} itemId={itemId} topic={topic} selectedItem={selectedItem} >
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={false}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveSite}
                    tabs={tabs}
                    itemPath={itemPath}
                    initialValues={currentItem}>
                    <SiteCard />
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
    </>
}

export default Site;
